import { ref, computed, watch, onMounted, onUnmounted, readonly, type Ref } from 'vue'
import type { PlanData, GenerationStatus } from '../lib/types'
import type { ScoreResult } from '@/features/scoring/lib/types'
import { tryParse } from '../lib/planHelpers'
import { buildPregnancyPrompt } from '../lib/pregnancyPrompt'
import type { PregnancyPhase } from '../lib/pregnancyPrompt'
import { repairPlanSchedule, validatePlanSchedule } from '../lib/planValidation'
import { ensureLastScoreHydrated, getLatestScoreFromHistory } from '@/shared/utils/lastScore'

const PROXY_URL = 'https://groq-proxy.simpson-sean-h.workers.dev'
const MODELS = ['openai/gpt-oss-120b', 'openai/gpt-oss-20b', 'llama-3.1-8b-instant'] as const

interface GroqAttemptError extends Error {
  code?: number
  apiCode?: string
  retryNextModel?: boolean
}

function isRateLimitMessage(msg: string): boolean {
  const lower = msg.toLowerCase()
  return lower.includes('rate limit') || lower.includes('rate limited') || lower.includes('all models rate limited') || msg.includes('429')
}

function groqError(message: string, code?: number, apiCode?: string): GroqAttemptError {
  const error = new Error(message) as GroqAttemptError
  error.code = code
  error.apiCode = apiCode
  error.retryNextModel = shouldRetryNextModel(code, message, apiCode)
  return error
}

function shouldRetryNextModel(status: number | undefined, message: string, apiCode = ''): boolean {
  if (status === 429) return true
  const lower = message.toLowerCase()
  const lowerApiCode = apiCode.toLowerCase()
  if (lowerApiCode.startsWith('model_permission_blocked')) return true
  if ((status === 400 || status === 404) && lower.includes('model')) {
    return lower.includes('deprecated')
      || lower.includes('decommission')
      || lower.includes('not found')
      || lower.includes('does not exist')
      || lower.includes('invalid')
      || lower.includes('not supported')
  }
  return false
}

function dateToLocalISO(date: Date): string {
  return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0')
}

function defaultPlanStartDate(): string {
  const start = new Date()
  start.setDate(start.getDate() + 1)
  return dateToLocalISO(start)
}

function isoDayOfWeek(iso: string): number {
  const date = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(date.getTime())) return 1
  return ((date.getDay() + 6) % 7) + 1 // 1=Mon ... 7=Sun
}

interface UseAIPlanOptions {
  scoreOverride?: Readonly<Ref<ScoreResult | null>>
  persistActivePlan?: boolean
  syncActivePlan?: boolean
  saveToSelectedUfpmMember?: boolean
  onPlanGenerated?: (plan: PlanData) => void
}

export function useAIPlan(options: UseAIPlanOptions = {}) {
  const persistActivePlan = options.persistActivePlan ?? true
  const syncActivePlan = options.syncActivePlan ?? persistActivePlan
  const saveToSelectedUfpmMember = options.saveToSelectedUfpmMember ?? false
  // Form state
  const testDate    = ref('')
  const planStartDate = ref(defaultPlanStartDate())
  const focus       = ref('balanced')
  const level       = ref('intermediate')
  const daysPerWeek = ref(5)
  const injuries    = ref('')
  const equipment   = ref<'bodyweight' | 'gym'>('bodyweight')

  // Pregnancy / postpartum mode
  const isPregnancyMode = ref(false)
  const pregnancyPhase  = ref<PregnancyPhase | ''>('')

  // Score reactivity — refreshed on ptr:score-ready (dispatched by vanilla updateAISummary)
  // Also falls back to latest from af_pt_history so a previously *saved* score (visible on
  // dashboard Quick Stats after reload/clear/plan clear) can be used for plan generation
  // without forcing the user to re-record the full test every time.
  if (!options.scoreOverride) ensureLastScoreHydrated()
  const lastScore = ref<ScoreResult | null>(
    options.scoreOverride?.value ?? window.lastScore ?? getLatestScoreFromHistory() ?? null
  )

  // Generation state
  const status       = ref<GenerationStatus>('idle')
  const rawText      = ref('')
  const planData     = ref<PlanData | null>(null)
  const planSummary  = ref('')
  const parseStatus  = ref('')
  const errorMsg     = ref('')
  const currentModel = ref('')

  const hasScore  = computed(() => lastScore.value !== null)
  const isLoading = computed(() => status.value === 'loading')

  watch(isPregnancyMode, enabled => {
    if (!enabled) pregnancyPhase.value = ''
  })

  function onScoreReady() {
    if (options.scoreOverride) {
      lastScore.value = options.scoreOverride.value
      return
    }
    ensureLastScoreHydrated()
    lastScore.value = window.lastScore ?? getLatestScoreFromHistory() ?? null
    if (lastScore.value && !window.lastScore) {
      window.lastScore = lastScore.value
    }
  }

  onMounted(() => {
    if (options.scoreOverride) {
      lastScore.value = options.scoreOverride.value
      return
    }
    window.addEventListener('ptr:score-ready', onScoreReady)
    window.addEventListener('storage', (e) => {
      if (e.key === 'af_pt_history') onScoreReady()
    })
    // In case we mounted before dashboard had a chance to hydrate
    if (!lastScore.value) {
      ensureLastScoreHydrated()
      lastScore.value = window.lastScore ?? getLatestScoreFromHistory() ?? null
    }
  })
  onUnmounted(() => {
    if (options.scoreOverride) return
    window.removeEventListener('ptr:score-ready', onScoreReady)
    // storage listener is cheap, no remove for simplicity
  })

  async function generate() {
    // Validation
    if (isPregnancyMode.value) {
      if (!pregnancyPhase.value) {
        errorMsg.value = 'Please select a pregnancy or postpartum phase to generate a specialized plan.'
        status.value = 'error'
        return
      }
    } else {
      if (!lastScore.value) {
        window.showToast?.('Calculate a score first on the Dashboard tab.')
        return
      }
    }
    if (!isPregnancyMode.value && !testDate.value) {
      window.showToast?.('Enter the official test date before generating a plan.')
      return
    }

    // Plan start defaults to the day immediately after generation, but users can adjust it.
    // This enables partial first calendar week (Mon-Sun alignment) and the rest-day rules.
    let selectedPlanStartDate = ''
    let startDayOfWeek = 1
    if (!isPregnancyMode.value) {
      selectedPlanStartDate = planStartDate.value || defaultPlanStartDate()
      planStartDate.value = selectedPlanStartDate
      startDayOfWeek = isoDayOfWeek(selectedPlanStartDate)
    }

    status.value      = 'loading'
    rawText.value     = ''
    planData.value    = null
    planSummary.value = ''
    parseStatus.value = ''
    errorMsg.value    = ''

    // Prompt construction — branch on mode
    let prompt: string
    if (isPregnancyMode.value) {
      prompt = buildPregnancyPrompt({
        phase:     pregnancyPhase.value as PregnancyPhase,
        injuries:  injuries.value || undefined,
        equipment: equipment.value,
      })
    } else {
      const built = window.buildAIPrompt?.({
        score:       lastScore.value,
        testDate:    testDate.value,
        focus:       focus.value,
        level:       level.value,
        daysPerWeek: daysPerWeek.value,
        injuries:    injuries.value,
        equipment:   equipment.value,
        planStartDate: selectedPlanStartDate,
        startDayOfWeek,
      })
      if (!built) {
        errorMsg.value = 'Could not build prompt — ensure a score is calculated first.'
        status.value = 'error'
        return
      }
      prompt = built
    }

    // Extracted helper: one attempt against a single Groq model
    async function tryGroqFetch(model: string): Promise<string> {
      const res = await fetch(PROXY_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          messages:        [{ role: 'user', content: prompt }],
          max_tokens:      8192,
          temperature:     0.0,
          response_format: { type: 'json_object' },
        }),
      })
      if (res.status === 429) throw groqError('Rate limit hit - retrying...', 429)
      if (!res.ok) {
        let msg = 'API error ' + res.status
        let apiCode = ''
        try {
          const d = await res.json() as { error?: { message?: string, code?: string } }
          msg = d.error?.message ?? msg
          apiCode = d.error?.code ?? ''
        } catch (_) { /* ignore parse failure on error body */ }
        if (res.status === 403 && !apiCode.startsWith('model_permission_blocked')) {
          throw new Error('Access denied - open ptreadiness.app to generate plans.')
        }
        throw groqError(msg, res.status, apiCode)
      }
      const data = await res.json() as { choices?: Array<{ message?: { content?: string } }> }
      const text = data.choices?.[0]?.message?.content ?? ''
      if (!text) throw new Error('Empty response from model.')
      return text
    }

    try {
      let fullText = ''
      for (const model of MODELS) {
        try {
          currentModel.value = model
          fullText = await tryGroqFetch(model)
          break
        } catch (e: unknown) {
          const err = e as GroqAttemptError
          if (err.retryNextModel && model !== MODELS[MODELS.length - 1]) {
            await new Promise(r => setTimeout(r, 1500))
            continue
          }
          throw e
        }
      }
      if (!fullText) throw new Error('All models rate limited. Wait a minute and try again.')
      rawText.value = fullText

      const parsed = tryParse(fullText)

      // Normalize phase_templates → week_template (LLM sometimes returns this shape)
      if (parsed) {
        const pt = (parsed as unknown as Record<string, unknown>).phase_templates as
          Record<string, { week_template?: unknown[] }> | undefined
        if (pt) {
          const p1 = pt.phase1?.week_template
          const p2 = pt.phase2?.week_template
          if (p1 && Array.isArray(p1)) {
            parsed.week_template = p1 as typeof parsed.week_template
            if (p2 && Array.isArray(p2)) {
              parsed.week_template_phase2 = p2 as typeof parsed.week_template
            }
          }
        }
      }

      if (!parsed || !Array.isArray(parsed.week_template)) {
        status.value = 'parse-error'
        return
      }

      if (!isPregnancyMode.value) {
        repairPlanSchedule(parsed, {
          requestedDaysPerWeek: Number(daysPerWeek.value),
          startDayOfWeek,
        })
        const scheduleErrors = validatePlanSchedule(parsed, {
          requestedDaysPerWeek: Number(daysPerWeek.value),
          startDayOfWeek,
        })
        if (scheduleErrors.length > 0) {
          throw new Error('Generated plan schedule did not match your settings: ' + scheduleErrors.join(' '))
        }
      }

      if (parsed._partial) {
        parseStatus.value = 'Warning: Partial parse — phases/targets unavailable but workout days loaded.'
      }

      parsed.official_test_date = testDate.value || parsed.official_test_date || ''
      parsed.generated_at = parsed.generated_at ?? new Date().toISOString()
      // Set before persistence/sync so every consumer reloads the same partial-first-week plan.
      if (!parsed.plan_start_date && selectedPlanStartDate) {
        parsed.plan_start_date = selectedPlanStartDate
      }

      if (persistActivePlan) {
        try { localStorage.setItem('af_pt_active_plan', JSON.stringify(parsed)) } catch (_) { /* quota */ }
        try { localStorage.removeItem('af_pt_plan_checked') } catch (_) { /* ignore */ }

        // Sync currentPlanData on the vanilla side so export/activate delegates work
        window.setPlanData?.(parsed)
      }

      // Firestore sync if signed in
      const pu = window.authService?.currentUser
      if (syncActivePlan && pu && window.syncService) {
        window.syncService.syncPlan(pu.uid, parsed).catch(() => { /* silent */ })
      }

      // UFPM: auto-save to selected member if one is active
      if (saveToSelectedUfpmMember && window.ufpmTools?.savePlanForSelectedMember?.(parsed)) {
        const member = window.ufpmTools.getSelectedMember?.()
        if (member) window.showToast?.('Plan saved for ' + member.fullName)
      }

      options.onPlanGenerated?.(parsed)

      const trainDays = parsed.week_template.filter(d => !d.is_rest).length
      planSummary.value = `${parsed.total_weeks} weeks | ${trainDays} training days/week | ${parsed.phases?.length ?? 0} phases`
      planData.value = parsed
      status.value   = 'success'
      window.showToast?.('Plan generated with ' + currentModel.value + '!')

    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      const isRL = isRateLimitMessage(msg)
      errorMsg.value = msg + (isRL ? ' — wait a moment and retry.' : '')
      status.value = 'error'
    } finally {
      currentModel.value = ''
    }
  }

  if (options.scoreOverride) {
    watch(options.scoreOverride, score => {
      lastScore.value = score
    })
  }

  return {
    // Form refs (writable by template)
    testDate, planStartDate, focus, level, daysPerWeek, injuries, equipment,
    isPregnancyMode, pregnancyPhase,
    // Read-only state
    lastScore:    readonly(lastScore),
    status:       readonly(status),
    rawText:      readonly(rawText),
    planData:     readonly(planData),
    planSummary:  readonly(planSummary),
    parseStatus:  readonly(parseStatus),
    errorMsg:     readonly(errorMsg),
    currentModel: readonly(currentModel),
    // Computed
    hasScore,
    isLoading,
    // Actions
    generate,
  }
}
